import React from 'react';
import {
  Container,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Button,
  useScrollTrigger,
  Slide,
} from '@mui/material';
import { ReactComponent as LogoTagline } from "../assets/logo.svg";

// Hide AppBar on scroll
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const TermsOfService = () => {
  return (
    <Box sx={{ minHeight: '100vh', background: '#f8f9fe' }}>
      {/* Sticky Navigation */}
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)'
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Button href="/" sx={{ p: 0 }}>
              <LogoTagline style={{ width: 'auto', height: '36px' }} />
            </Button>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
      <Toolbar /> {/* Spacer for fixed AppBar */}

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 6, fontWeight: 800 }}>
          Terms of Service
        </Typography>

        <Typography variant="body1" sx={{ mb: 4 }}>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          1. Agreement to Terms
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          By accessing or using Sashy's services, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access our service.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          2. Description of Service
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Sashy provides an AI-powered platform for analyzing customer reviews and feedback to generate business insights and recommendations. Our service includes data analysis, visualization tools, and automated insights generation.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          3. User Accounts
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          When you create an account with us, you must provide accurate and complete information. You are responsible for:
        </Typography>
        <Box component="ul" sx={{ mb: 4, pl: 4 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Maintaining the security of your account credentials
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              All activities that occur under your account
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Notifying us immediately of any unauthorized access
            </Typography>
          </li>
        </Box>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          4. Subscription and Payments
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Some aspects of the Service are provided on a subscription basis. You agree to pay all fees in accordance with your selected plan. Fees are non-refundable except as required by law or as explicitly stated in our refund policy.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          5. User Data and Privacy
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Our collection and use of personal information in connection with the Service is as described in our Privacy Policy. You agree that we may use your data in accordance with our Privacy Policy.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          6. Intellectual Property
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          The Service and its original content, features, and functionality are owned by Sashy and are protected by international copyright, trademark, and other intellectual property laws. Our trademarks and trade dress may not be used without our prior written permission.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          7. Acceptable Use
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You agree not to:
        </Typography>
        <Box component="ul" sx={{ mb: 4, pl: 4 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Use the Service for any illegal purpose
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Attempt to gain unauthorized access to the Service
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Interfere with or disrupt the Service
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Upload or transmit malicious code
            </Typography>
          </li>
        </Box>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          8. Limitation of Liability
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          To the maximum extent permitted by law, Sashy shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          9. Disclaimer of Warranties
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          The Service is provided "as is" and "as available" without any warranties of any kind, either express or implied. We do not guarantee that the Service will be uninterrupted, secure, or error-free.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          10. Changes to Terms
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          We reserve the right to modify or replace these Terms at any time. We will provide notice of any changes by posting the new Terms on this page and updating the "Last updated" date.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          11. Governing Law
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Sashy is registered, without regard to its conflict of law provisions.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          12. Contact
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          For any questions about these Terms, please contact us at{' '}
          <a href="mailto:info@sashy.ai">info@sashy.ai</a>.
        </Typography>
      </Container>
    </Box>
  );
};

export default TermsOfService; 